/**
 * Aba Marca: nome, segmento, logo, cor, site e e-mail de contato.
 *
 * O logo e uma previa LOCAL: `URL.createObjectURL` gera um endereco temporario
 * para o arquivo escolhido e nada sobe para lugar nenhum. Os enderecos criados
 * ficam guardados e sao revogados juntos na desmontagem -- revogar ao trocar a
 * imagem quebraria "Descartar", que volta para o endereco anterior.
 *
 * A previa a esquerda le o RASCUNHO (nome, cor, imagem), nao o dado salvo: a
 * pessoa ve o efeito antes de decidir salvar. Erros de validacao so aparecem
 * depois da primeira tentativa de salvar, para o formulario nao abrir
 * vermelho; a partir dai a barra lista cada campo com atalho de foco (ids
 * estaveis via `htmlFor`).
 */

import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { Button, CompanyLogo, Field, Input, SectionCard } from '../../../components/ui';
import type { BrandSettings } from '../types';
import { useRascunho } from '../useRascunho';
import { emailValido, iniciaisDe, siteValido } from '../vocabulario';
import { BarraSalvar, type ErroDeCampo } from './BarraSalvar';
import { SeletorDeTom } from './SeletorDeTom';

const LOGO_MAX_BYTES = 1_000_000;
const NOME_PADRAO = 'Sua empresa';
const ID_NOME = 'cfg-marca-nome';
const ID_SITE = 'cfg-marca-site';
const ID_EMAIL = 'cfg-marca-email';

type CampoValidado = 'name' | 'contactEmail' | 'website';

const ID_DO_CAMPO: Readonly<Record<CampoValidado, string>> = { name: ID_NOME, contactEmail: ID_EMAIL, website: ID_SITE };
const ROTULO_DO_CAMPO: Readonly<Record<CampoValidado, string>> = { name: 'Nome da empresa', contactEmail: 'E-mail de contato', website: 'Site' };

type Erros = Readonly<Partial<Record<CampoValidado, string>>>;

function validar(marca: BrandSettings): Erros {
  const erros: Partial<Record<CampoValidado, string>> = {};
  if (marca.name.trim() === '') {
    erros.name = 'Informe o nome da empresa.';
  }
  if (!emailValido(marca.contactEmail)) {
    erros.contactEmail = 'Informe um e-mail válido.';
  }
  if (!siteValido(marca.website)) {
    erros.website = 'Informe um endereço válido, como https://www.suaempresa.com.br.';
  }
  return erros;
}

const ORDEM: readonly CampoValidado[] = ['name', 'website', 'contactEmail'];

export function AbaMarca({ base }: { readonly base: BrandSettings }): JSX.Element {
  const rascunho = useRascunho(base);
  const [tentouSalvar, setTentouSalvar] = useState(false);
  const [erroLogo, setErroLogo] = useState<string | null>(null);
  const urlsCriadas = useRef<string[]>([]);

  useEffect(
    () => () => {
      for (const url of urlsCriadas.current) {
        URL.revokeObjectURL(url);
      }
    },
    [],
  );

  const { draft } = rascunho;
  const erros = validar(draft);
  const lista: readonly ErroDeCampo[] = ORDEM.flatMap((campo) => {
    const mensagem = erros[campo];
    return mensagem === undefined ? [] : [{ id: ID_DO_CAMPO[campo], campo: ROTULO_DO_CAMPO[campo], mensagem }];
  });
  const temErro = lista.length > 0;
  const erroVisivel = (campo: CampoValidado): string | null => (tentouSalvar ? (erros[campo] ?? null) : null);

  const onArquivo = (event: ChangeEvent<HTMLInputElement>): void => {
    const arquivo = event.target.files?.[0];
    if (arquivo === undefined) {
      return;
    }
    if (arquivo.size > LOGO_MAX_BYTES) {
      setErroLogo('Escolha uma imagem de até 1 MB.');
      event.target.value = '';
      return;
    }
    const url = URL.createObjectURL(arquivo);
    urlsCriadas.current.push(url);
    setErroLogo(null);
    rascunho.patch({ logoUrl: url });
  };

  const salvar = (): void => {
    setTentouSalvar(true);
    if (!temErro) {
      rascunho.salvar();
      setTentouSalvar(false);
    }
  };

  const descartar = (): void => {
    setTentouSalvar(false);
    setErroLogo(null);
    rascunho.descartar();
  };

  const nomeDaPrevia = draft.name.trim() === '' ? NOME_PADRAO : draft.name.trim();
  const previa = {
    name: nomeDaPrevia,
    logoInitials: iniciaisDe(nomeDaPrevia),
    logoTone: draft.logoTone,
    logoUrl: draft.logoUrl,
  };

  return (
    <SectionCard id="marca" title="Identidade da empresa" icon="empresa" description="Como a empresa aparece no painel, na ligação da IA e nas mensagens da régua.">
      <div className="grid gap-6 lg:grid-cols-[16rem_minmax(0,1fr)]">
        <aside aria-label="Prévia da marca" className="flex flex-col items-center gap-3 rounded-2xl border border-line bg-elevated p-5 text-center">
          <CompanyLogo company={previa} size="xl" />
          <p className="text-base font-semibold text-ink">{previa.name}</p>
          <p className="text-xs text-ink-muted">{draft.segment.trim() === '' ? 'Segmento' : draft.segment}</p>
          <p className="text-xs text-ink-subtle">É assim que o cabeçalho e a Central mostram a empresa.</p>
        </aside>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Nome da empresa" required htmlFor={ID_NOME} error={erroVisivel('name')} hint="Preenche a variável {empresa} na ligação da IA e nas mensagens.">
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                value={draft.name}
                onChange={(event) => rascunho.patch({ name: event.target.value })}
                autoComplete="organization"
              />
            )}
          </Field>

          <Field label="Segmento" hint="Uma linha: o ramo em que a empresa atua.">
            {({ id, describedBy }) => (
              <Input id={id} aria-describedby={describedBy} value={draft.segment} onChange={(event) => rascunho.patch({ segment: event.target.value })} />
            )}
          </Field>

          <Field label="Imagem do logo" hint="PNG, JPG, SVG ou WebP de até 1 MB. Na prévia, a imagem fica só neste navegador." error={erroLogo} className="sm:col-span-2">
            {({ id, describedBy, invalid }) => (
              <div className="flex flex-wrap items-center gap-3">
                <input
                  id={id}
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml,image/webp"
                  aria-describedby={describedBy}
                  aria-invalid={invalid}
                  onChange={onArquivo}
                  className="block h-11 w-full max-w-md cursor-pointer rounded-lg border border-line bg-surface text-sm text-ink-muted shadow-soft file:mr-3 file:h-full file:cursor-pointer file:border-0 file:border-r file:border-line file:bg-elevated file:px-4 file:text-sm file:font-medium file:text-ink"
                />
                {draft.logoUrl !== null ? (
                  <Button variant="ghost" icon="lixeira" onClick={() => rascunho.patch({ logoUrl: null })}>
                    Usar iniciais
                  </Button>
                ) : null}
              </div>
            )}
          </Field>

          <div className="sm:col-span-2">
            <SeletorDeTom value={draft.logoTone} onChange={(tone) => rascunho.patch({ logoTone: tone })} />
          </div>

          <Field label="Site" htmlFor={ID_SITE} error={erroVisivel('website')} hint="Referência da loja nas mensagens da régua.">
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                type="url"
                inputMode="url"
                aria-describedby={describedBy}
                aria-invalid={invalid}
                value={draft.website}
                onChange={(event) => rascunho.patch({ website: event.target.value })}
                placeholder="https://www.suaempresa.com.br"
                autoComplete="url"
              />
            )}
          </Field>

          <Field label="E-mail de contato" required htmlFor={ID_EMAIL} error={erroVisivel('contactEmail')} hint="Para onde o Converte Fácil escreve sobre a conta.">
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                type="email"
                inputMode="email"
                aria-describedby={describedBy}
                aria-invalid={invalid}
                value={draft.contactEmail}
                onChange={(event) => rascunho.patch({ contactEmail: event.target.value })}
                autoComplete="email"
              />
            )}
          </Field>
        </div>
      </div>

      <BarraSalvar
        className="mt-6"
        dirty={rascunho.dirty}
        saved={rascunho.saved}
        onSave={salvar}
        onDiscard={descartar}
        bloqueado={tentouSalvar && temErro}
        motivoBloqueio="Corrija os campos destacados."
        erros={lista}
      />
    </SectionCard>
  );
}
